/**
 * The galaxy map as data (M3 plan R9, spec §6.1): nodes with fog, overlay colours and route flags, relay edges between
 * visible systems, and the label list. R14 only draws what this writes.
 *
 * `out` is reused across builds: node, edge and label objects are recycled, so a rebuild after a state change
 * allocates only when the galaxy grows. Unknown systems stay in `nodes` (one slot per system, for instancing) with
 * `visible: false`, and are never labelled or joined by an edge.
 */
import type { Galaxy, RegionId } from '../../galaxy/types'
import type { Knowledge, ReachState, RelayStatus } from '../../reach/types'
import { colorNumber, dangerColor, type ReachMapsConfig } from './config'
import { knowledgeOf, mapToThree, relayStatusOf, systemIndexOf, type DangerFn } from './knowledge'
import { cssColorNumber, isBuiltinOverlay, type BuiltinOverlay, type MapOverlayLayer } from './overlays'

/** M3's built-ins, or the id of a registered `MapOverlayLayer` (M4 plan I7). */
export type GalaxyOverlay = BuiltinOverlay | (string & {})

export interface GalaxyNode {
  index: number
  id: string
  /** Map space (three.js axes, map units). */
  x: number
  y: number
  z: number
  knowledge: Knowledge
  visible: boolean
  /** Rumoured: drawn dim. */
  dim: boolean
  region: RegionId
  danger: number
  /** Overlay tint, 0xRRGGBB. */
  color: number
  radius: number
  current: boolean
  onRoute: boolean
  /** Position along the route (0 = the start), −1 when off it. */
  routeOrder: number
  /** A registered overlay's glyph for this system ('' = none); shown after the label. */
  badge: string
}

export interface GalaxyEdge {
  relayIndex: number
  relayId: string
  /** System indices of the two ends. */
  a: number
  b: number
  status: RelayStatus
  long: boolean
  color: number
  onRoute: boolean
  /** Relative width (1 = the thin line; a registered overlay may widen it). */
  width: number
  /** Drawn dashed (offline relays, or a registered overlay's choice). */
  dashed: boolean
}

export interface GalaxyLabel {
  systemIndex: number
  text: string
  /** Higher wins in `labelCull`: current 4, route 3, charted 2, rumoured 1. */
  priority: number
}

export interface GalaxyView {
  nodes: GalaxyNode[]
  edges: GalaxyEdge[]
  labels: GalaxyLabel[]
  /** Visible route systems in order (system indices), for the route polyline. */
  route: number[]
  routeColor: number
  overlay: GalaxyOverlay
}

export interface GalaxyViewOptions {
  danger: DangerFn
  /** The system the player is in (the session layout's id), or null. */
  currentSystemId: string | null
  /** The route to flag; defaults to `state.route`. */
  route?: readonly string[] | null
  /** The registered layer for a non-built-in `overlay` (M4 plan I7); null or absent = the plain style. */
  layer?: MapOverlayLayer | null
}

export function createGalaxyView(): GalaxyView {
  return { nodes: [], edges: [], labels: [], route: [], routeColor: 0xffffff, overlay: 'region' }
}

const LABEL_PRIORITY = { current: 4, route: 3, charted: 2, rumoured: 1 } as const
const scratch = { x: 0, y: 0, z: 0 }

function node(out: GalaxyView, i: number): GalaxyNode {
  let n = out.nodes[i]
  if (n === undefined) {
    n = { index: i, id: '', x: 0, y: 0, z: 0, knowledge: 'unknown', visible: false, dim: false, region: 'gate', danger: 0, color: 0, radius: 0, current: false, onRoute: false, routeOrder: -1, badge: '' }
    out.nodes[i] = n
  }
  return n
}

function edge(out: GalaxyView, i: number): GalaxyEdge {
  let e = out.edges[i]
  if (e === undefined) {
    e = { relayIndex: 0, relayId: '', a: 0, b: 0, status: 'online', long: false, color: 0, onRoute: false, width: 1, dashed: false }
    out.edges[i] = e
  }
  return e
}

function label(out: GalaxyView, i: number): GalaxyLabel {
  let l = out.labels[i]
  if (l === undefined) {
    l = { systemIndex: 0, text: '', priority: 0 }
    out.labels[i] = l
  }
  return l
}

/** Fills `out` with the fogged galaxy for `state`, tinted by `overlay`. Returns `out`. */
export function galaxyView(galaxy: Galaxy, state: ReachState, overlay: GalaxyOverlay, cfg: ReachMapsConfig, out: GalaxyView, opts: GalaxyViewOptions): GalaxyView {
  const g = cfg.galaxy
  const systems = galaxy.systems
  const layer = isBuiltinOverlay(overlay) ? null : (opts.layer ?? null)
  // A registered overlay with no style for a system draws it in the offline grey ("no data").
  const noData = colorNumber(g.colors.offline)
  out.overlay = overlay
  out.routeColor = colorNumber(g.colors.route)

  for (let i = 0; i < systems.length; i++) {
    const s = systems[i]
    const n = node(out, i)
    const knowledge = knowledgeOf(state, s.id)
    mapToThree(s.mapPos, scratch)
    n.index = i
    n.id = s.id
    n.x = scratch.x
    n.y = scratch.y
    n.z = scratch.z
    n.knowledge = knowledge
    n.visible = knowledge !== 'unknown'
    n.dim = knowledge === 'rumoured'
    n.region = s.region
    n.danger = opts.danger(i)
    n.radius = knowledge === 'charted' ? g.nodeRadius.charted : knowledge === 'rumoured' ? g.nodeRadius.rumoured : 0
    n.badge = ''
    if (layer === null && !isBuiltinOverlay(overlay)) {
      n.color = noData
    } else if (layer === null) {
      n.color = colorNumber(overlay === 'region' ? g.colors.region[s.region] : dangerColor(n.danger, cfg))
    } else {
      const style = n.visible ? layer.node(s.id) : null
      if (style === null) {
        n.color = noData
      } else {
        n.color = cssColorNumber(style.color)
        n.radius *= style.size > 0 ? style.size : 1
        n.badge = style.badge ?? ''
      }
    }
    n.current = s.id === opts.currentSystemId
    n.onRoute = false
    n.routeOrder = -1
  }
  out.nodes.length = systems.length

  // Route flags and the polyline (hidden systems never show, even on a stale route).
  const route = opts.route === undefined ? state.route : opts.route
  out.route.length = 0
  if (route !== null) {
    for (let r = 0; r < route.length; r++) {
      const i = systemIndexOf(galaxy, route[r])
      if (i < 0 || !out.nodes[i].visible) continue
      out.nodes[i].onRoute = true
      out.nodes[i].routeOrder = r
      out.route.push(i)
    }
  }

  let edges = 0
  for (let r = 0; r < galaxy.relays.length; r++) {
    const relay = galaxy.relays[r]
    const a = systemIndexOf(galaxy, relay.a)
    const b = systemIndexOf(galaxy, relay.b)
    if (a < 0 || b < 0 || !out.nodes[a].visible || !out.nodes[b].visible) continue
    const e = edge(out, edges++)
    e.relayIndex = r
    e.relayId = relay.id
    e.a = a
    e.b = b
    e.status = relayStatusOf(state, relay)
    e.long = relay.long
    e.color = colorNumber(g.colors[e.status])
    e.width = 1
    e.dashed = e.status === 'offline'
    const style = layer === null ? null : layer.edge(relay.id)
    if (style !== null) {
      e.color = cssColorNumber(style.color)
      e.width = style.width > 0 ? style.width : 1
      e.dashed = style.dashed === true
    }
    const ra = out.nodes[a].routeOrder
    const rb = out.nodes[b].routeOrder
    e.onRoute = ra >= 0 && rb >= 0 && Math.abs(ra - rb) === 1
  }
  out.edges.length = edges

  let labels = 0
  for (let i = 0; i < systems.length; i++) {
    const n = out.nodes[i]
    if (!n.visible) continue
    const l = label(out, labels++)
    l.systemIndex = i
    l.text = n.badge === '' ? systems[i].name : `${systems[i].name} ${n.badge}`
    l.priority = n.current
      ? LABEL_PRIORITY.current
      : n.onRoute
        ? LABEL_PRIORITY.route
        : n.knowledge === 'charted'
          ? LABEL_PRIORITY.charted
          : LABEL_PRIORITY.rumoured
  }
  out.labels.length = labels
  return out
}
